// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
// RxJS
import { Observable, of } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
// Models
import { CompanyModel } from '../_models/company.model';

// Ngrx
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken } from '../../auth/_selectors/auth.selectors';
import { environment } from '../../../../environments/environment';
import { map } from 'rxjs/operators';

const API_URL = environment.USER_BASE;

@Injectable()
export class CompaniesService {
  private authToken;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
  }

  // CREATE =>  POST: add a new company to the server
  createCompany(company: CompanyModel): Observable<CompanyModel> {
    const url = API_URL + '/c-company';
    const body = {
      company: {
        isActive: company.isActive,
        limitNumberOffices: company.limitNumberOffices,
        limitNumberUsers: company.limitNumberUsers,
        measurementSystem: company.measurementSystem,
      },
      name: company.name,
      primaryContacts: company.primaryContacts,
      branding: {
        logoBase64: company.logoBase64,
        logoSquareBase64: company.logoSquareBase64,
        additionalLogoBase64: company.additionalLogoBase64,
        primaryColor1: company.primaryColor1,
        primaryColor2: company.primaryColor2,
        primaryColor3: company.primaryColor3,
        secondaryColor1: company.secondaryColor1,
        secondaryColor2: company.secondaryColor2,
        secondaryColor3: company.secondaryColor3,
        termsUrl: company.termsUrl,
        privacyPolicyUrl: company.privacyPolicyUrl,
        contactUsUrl: company.contactUsUrl,
        tutorialsUrl: company.tutorialsUrl,
        serviceSurveyUrl: company.serviceSurveyUrl,
      },
    };
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<CompanyModel>(url, body, { headers: httpHeaders });
  }

  // READ
  getAllCompanies(): Observable<CompanyModel[]> {
    const url = API_URL + '/companies';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http
      .get<{ companies: CompanyModel[] }>(url, { headers: httpHeaders })
      .pipe(map((res) => res.companies));
  }

  // READ
  getCompaniesNoAdminRole(): Observable<CompanyModel[]> {
    const url = API_URL + '/company-no-admin-role';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<CompanyModel[]>(url, { headers: httpHeaders });
  }

  getCompanyById(companyId: number): Observable<CompanyModel> {
    if (!companyId) {
      return of(null);
    }
    const body = {
      id: companyId,
    };
    const url = API_URL + '/companies';
    return this.http.post<any>(url, body);
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  // Server should return filtered/sorted result
  findCompanies(queryParams: QueryParamsModel, companyId: number): Observable<QueryResultsModel> {
    const filter = queryParams.filter;
    const body = {
      id: companyId,
      pageSize: queryParams.pageSize,
      pageIndex: queryParams.pageNumber,
      searchData: filter.searchTerm,
      isActive: filter.isActive,
    };

    let sort = queryParams.sortField;

    if (sort === 'id') sort = 'company.id';
    if (sort === 'name') sort = 'company.name';
    if (sort === 'isActive') sort = 'company.is_active';
    if (sort === 'admin') sort = 'admin.name';

    let fromString = `sort=${sort},${queryParams.sortOrder}`;

    if (filter.id) fromString += `&id_has=${filter.id}`;
    if (filter.name) fromString += `&name_has=${filter.name}`;
    if (filter.admin) fromString += `&admin_has=${filter.admin}`;

    const params = new HttpParams({ fromString });

    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);

    const url = API_URL + `/companies`;

    return this.http.post<QueryResultsModel>(url, body, { headers: httpHeaders, params });
  }

  getCompaniesAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const filter = queryParams.filter;
    let sort = queryParams.sortField;

    if (sort === 'id') sort = 'company.id';
    if (sort === 'name') sort = 'company.name';
    if (sort === 'isActive') sort = 'company.is_active';
    if (sort === 'admin') sort = 'admin.name';

    let fromString = `sort=${sort},${queryParams.sortOrder}`;

    if (filter.isActive) fromString += `&is_active=${filter.isActive}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.admin) fromString += `&admin_has=${filter.admin}`;
    }

    const params = new HttpParams({ fromString });

    return this.http.get(`${API_URL}/companies/csv`, { params, responseType: 'text' });
  }

  // UPDATE => PUT: update the company on the server
  updateCompany(company): Observable<any> {
    const url = API_URL + `/company`;
    const body = {
      company: {
        id: company.id,
        isActive: company.isActive,
        limitNumberOffices: company.limitNumberOffices,
        limitNumberUsers: company.limitNumberUsers,
        measurementSystem: company.measurementSystem,
      },
      name: company.name,
      primaryContacts: company.primaryContacts,
      branding: {
        logoBase64: company.logoBase64,
        logoSquareBase64: company.logoSquareBase64,
        additionalLogoBase64: company.additionalLogoBase64,
        primaryColor1: company.primaryColor1,
        primaryColor2: company.primaryColor2,
        primaryColor3: company.primaryColor3,
        secondaryColor1: company.secondaryColor1,
        secondaryColor2: company.secondaryColor2,
        secondaryColor3: company.secondaryColor3,
        termsUrl: company.termsUrl,
        privacyPolicyUrl: company.privacyPolicyUrl,
        contactUsUrl: company.contactUsUrl,
        tutorialsUrl: company.tutorialsUrl,
        serviceSurveyUrl: company.serviceSurveyUrl,
      },
    };
    return this.http.put(url, body);
  }

  // UPDATE Status
  updateStatusForCompany(id: number, isActive: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = API_URL + '/companies/' + id + '/set-active-state';
    return this.http.put(url, { active: isActive }, { headers: httpHeaders });
  }

  // UPDATE Status
  updateStatusForCompanies(ids: number[], isActive: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids,
      isActive,
    };
    const url = API_URL + '/companystatusupdate';
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // DELETE => delete the company from the server
  deleteCompany(companyId: number): Observable<any> {
    const url = API_URL + '/company/' + companyId;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);

    return this.http.delete<any>(url, { headers: httpHeaders });
  }

  deleteCompanies(companiesIds: number[] = []): Observable<any> {
    const url = API_URL + '/multiple-soft-delete-company';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids: companiesIds,
    };
    return this.http.post<any>(url, body, { headers: httpHeaders });
  }

  searchCompanies(search_term: string, fromAutoComplete?: boolean) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = API_URL + `/companies`;
    const body = {
      searchData: search_term,
      id: 0,
      isActive: fromAutoComplete ? '' : true,
      pageIndex: 0,
      pageSize: 10,
    };
    return this.http.post<any>(url, body, { headers: httpHeaders }).pipe(
      map((res) => {
        if (res.code === 200) {
          return res.pageOfItems;
        }
        return [];
      }),
    );
  }

  getTriggers(): Observable<CompanyModel[]> {
    const url = environment.NOTIFICATION_BASE + '/triggers';
    return this.http.get<any>(url);
  }
}
